import { PAYMENT_ORIGINS } from '@hotjar/webapp/domains/billing/constants';
import { predicate } from '@hotjar/webapp/lib/function';
import * as tracking from '@hotjar/webapp/tracking/common';
import * as headerTracking from '@hotjar/webapp/tracking/header';
import { store } from '@hotjar/webapp/tracking/common/tracking-context-store';
import * as adwords from '@hotjar/webapp/tracking/tools/adwords';
import * as facebook from '@hotjar/webapp/tracking/tools/facebook';
import * as googleAnalytics from '@hotjar/webapp/tracking/tools/google-analytics';
import * as hotjarTracking from '@hotjar/webapp/tracking/tools/hotjar';
import { setKnowledgeBaseCookie } from '@hotjar/webapp/tracking/tools/knowledge-base';
import { FormSubmittedEventData } from '@hotjar/webapp/tracking/tools/segment';
import { extractAccountGroupingProps, extractSiteGroupingProps } from '@hotjar/webapp/tracking/utils';
import { onTrackLogin } from '@hotjar/webapp/features/Login';
import {
    authenticationModalClosed,
    authenticationModalRendered,
    authenticationModalCustomerCancelled,
    authenticationNeeded,
    verify3DSCalled,
    verify3DSChallenge,
    payment3DS2AuthenticationLookup
} from '@hotjar/checkout';
// eslint-disable-next-line no-redeclare
import _ from 'lodash';
import { getDeviceType } from '@hotjar/webapp/hooks/useDeviceType';

/**
 * Facade class containing common tracking operations involving vendor-specific tracking modules.
 */
angular
    .module('hjApp.tracking')
    .service('Track', function ($rootScope, $q, HotjarTracking, Segment, SessionStorage, UserData) {
        /*************************
         * INITIALIZATION
         *************************/
        var self = this;

        trackLogin();

        /*************************
         * PRIVATE METHODS
         *************************/

        /**
         * Decorator to validate that a list of given arguments are provided.
         * If any of the given arguments are `null` or `undefined` then the
         * decorated function is not called, and a given default value is
         * returned.
         *
         * Note: List or arguments actually doesn't matter, the validation is
         * primarily a comparison of array lengths, but they're super useful for
         * the debug message.
         *
         * @param fn {Function} The function to call with valid arguments
         * @param properties {string[]} The arguments to validate
         * @param defaultVal The value to return if the properties fail to
         *                   validate.
         */
        const validateArguments = (fn, properties, defaultVal = undefined) =>
            predicate(
                fn,
                [
                    (...args) => {
                        const invalidArguments = _.zip(properties, args)
                            .filter(([, value]) => _.isUndefined(value) || _.isNull(value))
                            .map(([name]) => name);

                        const isValid = invalidArguments.length === 0;

                        if (!isValid) {
                            logTrackingError(
                                `${fn.name} -- One or more required properties are missing: ${invalidArguments.join(
                                    ', '
                                )}`
                            );
                        }

                        return isValid;
                    }
                ],
                defaultVal
            );

        /**
         * Track a user login.
         */
        function trackLogin() {
            var trackLogin = SessionStorage.get('trackLogin');

            if (trackLogin) {
                SessionStorage.remove('trackLogin');
                facebook.trackLogin();
            }
        }

        const trackSignupStepProgress = (action, step, signupCampaignData, signupMethod) => {
            const stepNumber = typeof step === 'number' ? step : -1;
            const stepName = typeof step === 'string' ? step : '';

            const data = {
                ...signupCampaignData,
                ...(signupMethod && { signup_method: signupMethod }),
                signup_step_number: stepNumber,
                ...(stepName && { signup_step: stepName })
            };
            const eventName = `Signup Step ${step} ${action}`;
            googleAnalytics.trackAccountEvent(eventName, signupMethod);
            hotjarTracking.tagRecording(eventName);
            Segment.track(`Signup Step ${action}`, data);
        };

        /*************************
         * GROUPING AND VALIDATION
         *************************/

        /**
         * Get tracking group data for a site.
         *
         * @param site Site to get group data for.
         * @return {object} Object referencing the site id, organization id and account id.
         */
        this.getGroupDataForSite = extractSiteGroupingProps;

        /**
         * Get tracking group data for an organization.
         *
         * @param account Account to get group data for.
         * @return {object} Object referencing the site id, organization id and account id.
         */
        this.getGroupDataForAccount = extractAccountGroupingProps;

        /**
         * Validate the data we will be sending with an event.
         * NOTE: The only validation at the moment, is that all properties defined
         * should be defined.
         *
         * @param eventName {string} Name of event.
         * @param properties {array} Collection of properties to validate.
         * @return {boolean} True if data is valid, False otherwise.
         */
        function validateEventData(eventName, properties) {
            const invalid = _.some(properties, (prop) => _.isUndefined(prop) || _.isNull(prop));

            // If invalid show console error.
            // NOTE: We don't throw an error intentionally,
            // as we don't want metrics issues to impact user experience.
            if (invalid) {
                logTrackingError(`${eventName} -- One or more required properties are missing.`);
                return false;
            }

            return true;
        }

        /**
         * Output a console.error when a tracking failure occurs.
         * NOTE: This still allows the code to keep executing since we don't
         * Throw an error, but rather simply log it to console.
         *
         * @param message {string} Message to output.
         */
        function logTrackingError(message) {
            /* eslint-disable no-console */
            if (console && typeof console.error === 'function') {
                console.error('TRACKING FAILURE: ' + message);
            }
            /* eslint-enable no-console */
        }

        /*************************
         * PUBLIC METHODS
         *************************/

        /*************************
         * FEATURE RELATED EVENTS
         *************************/
        this.getAnonymousUserID = function getAnonymousUserID() {
            return Segment.getAnonymousUserID().then((id) => id || $q.reject());
        };

        this.setAnonymousUserID = function setAnonymousUserID(anonymousID) {
            return Segment.setAnonymousUserID(anonymousID);
        };

        this.getUserID = function getUserID() {
            return Segment.getUserID().then((id) => id || $q.reject());
        };

        this.isIdentified = function () {
            return Segment.isIdentified().catch(() => false);
        };
        /**
         * Track the start of a tool creation.
         *
         * @param toolName Name of the tool, e.g. 'Heatmap'
         * @param toolCreatedFrom 'new' or 'duplicate' if the tool was duplicated from another one.
         * @param properties the additional properties to track along with the event.
         */
        this.toolCreationStarted = function toolCreationStarted(toolName, toolCreatedFrom, properties) {
            let data = self.getGroupDataForSite(UserData.currentSite);
            data['tool_created_from'] = toolCreatedFrom;
            _.extend(data, properties);

            Segment.track(toolName + ' Creation Started', data);
        };

        /**
         * Track in Segment that an Optimizely experiment has been viewed.
         */

        this.experimentViewed = (experimentId, experimentTargetId, variationId, userId = UserData.get('userId')) =>
            tracking.experimentViewed({
                experimentId,
                experimentTargetId,
                variationId,
                userId,
                site: UserData.currentSite
            });

        this.experimentAssigned = function experimentAssigned(experiment, target, variation, userId) {
            this.tagRecording(`${experiment}_${variation}`);
            this.experimentViewed(experiment, target, variation, userId);
        };

        /**
         * Track a virtual page view.
         *
         * @param path Virtual Page View path.
         */
        this.virtualPageView = function (path) {
            hotjarTracking.trackVirtualPageView(path);
        };

        /**
         * Execute a trigger. Not exactly an event, but this is the best place for it either way.
         *
         * @param {string} name Name of trigger to execute.
         * @param {boolean} [triggerOnStateChange = false] Defer the trigger until $stateChangeSuccess.
         */
        this.trigger = function (name, triggerOnStateChange) {
            if (triggerOnStateChange) {
                HotjarTracking.triggerOnStateChangeSuccess(name);
            } else {
                hotjarTracking.trigger(name);
            }
        };

        /**
         * Tag a recording.
         *
         * @param name Name of tag.
         */
        this.tagRecording = function (name) {
            hotjarTracking.tagRecording(name);
        };

        /**
         * Track that a new item has been created.
         * NOTE: This event is specifically for when the new item is one of our 7 features.
         *
         * @param item Name of created item, e.g. 'Funnel'.
         * @param onboarding Boolean indicating that the creation was part of the onboarding flow.
         */
        this.create = function (item, onboarding) {
            var triggerName = item.toLowerCase().replace(' ', '_') + '_created';
            self.trigger(onboarding ? triggerName + '_onboarding' : triggerName, true);
            self.tagRecording(onboarding ? 'Created Onboarding ' + item : 'Created ' + item);

            self.trigger(triggerName, true);
            self.tagRecording('Created ' + item);

            googleAnalytics.trackCreatedItemEvent('Created ' + item);
        };

        /**
         * Track that a particular item has been viewed.
         * NOTE: This event is specifically for reports and should not be used for anything else.
         * NOTE: This was used to tag events to Intercom, which is no obsolete. Method is kept for compatibility.
         * @param item Name of viewed item, e.g. 'Funnel'.
         */
        // eslint-disable-next-line no-unused-vars
        this.view = function (item) {
            // This currently does not track any metrics.
        };

        /**
         * Track when a user is invited to an organization.
         * NOTE: This was used to tag events to Intercom, which is now obsolete. Method is kept for compatibility.
         */
        this.userInvited = function () {
            // This currently does not track any metrics.
        };

        /**
         * Track that a particular item has been browsed.
         * NOTE: This event is specifically for states and pages browsed to.
         *
         * @param item Name of item, e.g. 'Recordings List'.
         * @param data Extra data object, optional.
         */
        this.browse = function (item, data) {
            // The recordings list is the only one we sent through JS as
            // we want to only send it once when the state is loaded.
            if (item === 'Recordings List') {
                data = _.extend(data || {}, self.getGroupDataForSite(UserData.currentSite));
                Segment.track('Recording List Viewed', data);
            }
        };

        /**
         * Track that the state has changed.
         * NOTE: This event is specifically for tracking state changes.
         *
         * @param url URL of page.
         * @param fromState Angular UI-Router object describing the state before the change happened.
         * @param toState Angular UI-Router object describing the state after the change happened.
         */
        this.stateChange = function (url, fromState, toState) {
            // We do state redirects similar to `.../heatmap -> .../heatmap/list` in many parts of the application.
            // This causes many more state changes than what the user would experience as a "page change".
            //
            // The `finalState` boolean can be used to ignore intermittent state changes if you only
            // care about what would normally be perceived by a user as a "page change".
            //
            // The (horrible) heuristic is that a state needs a template and that template has
            // to be !== '<ui-view/>' (which we use for redirects) to be deemed as a `finalState`.
            const finalState = toState.template && toState.template !== '<ui-view/>';

            const getSiteIDFromURL = (url) => {
                if (!url || !url.match(/sites\/\d+\//g)) return null;
                return url.split('sites/')[1].split('/')[0];
            };

            if (finalState) {
                const siteID = getSiteIDFromURL(url);
                // Update last state history in the Knowledge Base cookie.
                setKnowledgeBaseCookie(siteID, toState);
            }
        };

        /**
         * Track when a modal has been loaded in view.
         *
         * @param name {string} Name of modal, e.g. 'Upsell'.
         * @param groupData {object} Group data properties for this event.
         * @param [data] {object} Additional data properties we want to send with the event.
         */
        this.loadedModal = tracking.loadedModal;

        /**
         * Track when a modal has been dismissed/closed.
         *
         * @param name {string} Name of modal, e.g. 'Upsell'.
         * @param groupData {object} Group data properties for this event.
         * @param [data] {object} Additional data properties we want to send with the event.
         */
        this.closedModal = tracking.closedModal;

        /**
         * Track when something was clicked.
         *
         * @param name {string} Name of element, e.g. 'Create poll button'.
         * @param uiPosition {string} UI location the event was triggered from, e.g. 'Navigation sitebar'.
         * @param groupData {object} Group data properties for this event.
         * @param [data={}] {object} Additional data properties we want to send with the event.
         */
        this.click = (name, uiPosition, groupData, data = {}) =>
            tracking.trackClick({ name, uiPosition, groupData, data });

        /**
         * Track when something was viewed.
         *
         * @param name {string} Name of element, e.g. 'Create poll button'.
         * @param uiPosition {string} UI location the event was triggered from, e.g. 'Navigation sitebar'.
         * @param groupData {object} Group data properties for this event.
         * @param [data={}] {object} Additional data properties we want to send with the event.
         */
        this.viewElement = (name, uiPosition, groupData, data = {}) =>
            tracking.viewElement({ name, uiPosition, groupData, data });

        /**
         * Track when a form is submitted.
         *
         * @param {string} name Name of form, e.g. 'Send tracking code via email'.
         * @param {boolean} submitSuccess True if the form was submit with a success response.
         * @param {object.<string,any>} groupData Group data properties for this event.
         * @param {object.<string,any>} [data] Additional data properties we want to send with the event.
         */
        this.formSubmit = validateArguments(
            function formSubmit(name, submitSuccess, groupData, data) {
                const eventName = 'App Form Submitted';

                // Make sure we have a Boolean.
                submitSuccess = Boolean(submitSuccess);

                data = FormSubmittedEventData(name, submitSuccess, _.extend({}, data || {}, groupData));

                self.tagRecording(`${eventName}: ${name} - ${submitSuccess ? 'Success' : 'Failed'}`);

                // Note: Track should _always_ resolve such that any errors in Track
                // won't affect UX, so handle any rejections.
                return Segment.track(eventName, data).then(undefined, _.identity);
            },
            ['name', 'submitSuccess', 'groupData'],
            $q.resolve()
        );

        /**
         * Track when a user shares an item.
         *
         * @param [data={}] {object} Properties to track.
         * @param [data.item] {string} The shared item.
         * @param [data.recordingId] {number} Recording ID that the event is triggered for.
         * @param [data.relevanceScore] {number} Score for the relevance of the recording to the user.
         * @param [data.sharedStatus] {('On'|'Off')} Sharing active or not active.
         */
        this.itemShared = ({ item, recordingId, relevanceScore, sharedStatus } = {}) =>
            tracking.itemShared({ item, recordingId, relevanceScore, sharedStatus }, UserData.currentSite);

        /**
         * Track an integration step.
         *
         * @param name {string} Name of integration, e.g. 'Google Tag Manager'.
         * @param type {?string} Type of integration, e.g. 'Script installation'.
         * @param step {string} Integration step name. e.g. 1 or 'Authentication'.
         * @param groupData {object} Group data properties for this event.
         * @param [data] {object} Additional data properties we want to send with the event.
         */
        this.integrationStep = (name, type, step, groupData, data) =>
            tracking.integrationStep({ name, type, step, groupData, data });

        /**
         * Track when a user uses on help/support related items in the UI.
         *
         * @param helpType Type of help the user is given, such as docs or widget.
         * @param [data] {object} Additional data properties we want to send with the event.
         */
        this.help = function (helpType, data) {
            var valid = validateEventData('Track.help', [helpType]);

            if (valid) {
                data = _.extend(data || {}, self.getGroupDataForSite(UserData.currentSite));
                data['help_type'] = helpType;

                self.tagRecording('Help: ' + helpType);
                Segment.track('Accessed Help', data);
            }
        };

        /**
         * Track a NEW ACCOUNT signup.
         *
         * @param plan The plan the user signed up to, e.g. 'business'.
         */
        this.newUserSignup = tracking.newUserSignup;

        /**
         * Track a completed login.
         */
        this.login = function () {
            // Since we need to process this event after we have already created the user,
            // store the plan in sessionStorage so we can use it when the user has been logged in.
            onTrackLogin();
        };

        /**
         * Actions to track / execute on logout.
         */
        this.logout = function () {
            return Segment.reset();
        };

        this.resetAndKeepAnonymousId = () =>
            this.getAnonymousUserID()
                .then((id) => this.logout().then(() => id))
                .then(this.setAnonymousUserID);

        /**
         * Alias a user with an anonymous ID.
         * @param userId {number} ID of user to associate anonymous user/events to.
         */
        this.alias = function alias(userId) {
            // Note: Track should _always_ resolve such that any errors in Track
            // won't affect UX, so handle any rejections.
            return Segment.alias(`${userId}`).then(undefined, _.identity);
        };

        /**
         * If user has already been identified, log them out (reset their tracking date).
         */
        this.logoutIfAlreadyIdentified = function () {
            Segment.logoutIfAlreadyIdentified();
        };

        /**
         * Track the attempt to verify the tracking code installation
         *
         * @param data {object} Additional data properties we want to send with the event.
         */
        this.trackingCodeVerificationRequest = tracking.trackingCodeVerificationRequest;

        this.trackingCodeVerificationAttempt = tracking.trackingCodeVerificationAttempt;

        this.trackingCodeInstallerOpened = tracking.trackingCodeInstallerOpened;

        /**
         * Track that the user has advanced to a specific step in the signup process.
         * NOTE: This is for the new test process.
         *
         * @param step {number|string} Step.
         * @param signupCampaignData {object} Campaign information.
         * @param [signupMethod] {string} Method user used to go through step 1.
         */
        this.signupStep = function (step, signupCampaignData, signupMethod) {
            const stepNumber = typeof step === 'number' ? step : -1;
            const stepName = typeof step === 'string' ? step : '';

            var data = {
                signup_step_number: stepNumber,
                ...(stepName && { signup_step: stepName }),
                signup_method: signupMethod,
                device_type: getDeviceType()
            };

            data = _.extend(data, signupCampaignData);

            // Signup method is defined in step 2.
            if (signupMethod) {
                data['signup_method'] = signupMethod;
            }

            googleAnalytics.trackAccountEvent('Signup Step ' + step);
            hotjarTracking.tagRecording('Signup Step ' + step);
            Segment.track('Signup Step', data);
        };

        this.signupStepSubmitted = function (step, signupCampaignData, signupMethod) {
            trackSignupStepProgress('Submitted', step, signupCampaignData, signupMethod);
        };

        this.signupStepCompleted = function (step, signupCampaignData, signupMethod) {
            trackSignupStepProgress('Completed', step, signupCampaignData, signupMethod);
        };

        this.emailVerificationRequest = function () {
            Segment.track('Verification Email Sent', {});
        };

        this.emailVerificationResult = function (isValid, method = 'link') {
            Segment.track('Email Verification Attempted', { is_valid: isValid, method });
        };

        /**
         * Track when the UI Tour has been completed or closed.
         * NOTE: This was used to tag events to Intercom, which is now obsolete. Method is kept for compatibility.
         */
        this.completedTour = function () {
            // This currently does not track any metrics.
        };

        /**
         * Track when the UI Tour steps.
         */
        this.tourStep = function (step) {
            Segment.track('Dashboard Overview Tour Step', {
                dashboard_overview_tour_step_number: step
            });
        };

        /**
         * Track the steps of the GTM integration.
         * @param step {string} Integration step name. e.g. 1 or 'Authentication'.
         */
        this.gtmIntegration = function (step) {
            self.integrationStep(
                'Google Tag Manager',
                'Script installation',
                step,
                self.getGroupDataForSite(UserData.currentSite)
            );
        };

        /**
         * Track the steps of the Optimizely integration.
         * @param step {string} Integration step name. e.g. 1 or 'Authentication'.
         */
        this.optimizelyIntegration = (step) => tracking.optimizelyIntegration(step, UserData.currentSite);

        /*************************
         * HEADER SECTION RELATED EVENTS
         *************************/
        /**
         * Track hover over site&organizations.
         */
        this.hoveredTrackingStateIcon = headerTracking.hoveredTrackingStateIcon;

        /**
         * Track user clicks on site name (directs the user to dashboard dashboard overview currently).
         */
        this.clickedSiteName = function () {
            return headerTracking.clickedSiteName(UserData.currentSite);
        };

        /**
         * Track user (on Basic) clicks on upsell copy to the right of the plan badge.
         */
        this.clickedUpsell = headerTracking.clickedUpsell;

        /**
         * Track user clicks on 'what's new'.
         */
        this.clickedWhatsNew = function () {
            return headerTracking.clickedWhatsNew(UserData.currentSite);
        };

        /**
         * Track user clicks on 'Help'.
         */
        this.clickedHelp = headerTracking.clickedHelp;

        /**
         * Track user clicks on Users Badge (has user icon and number of users).
         */
        this.clickedUsersBadge = function () {
            return headerTracking.clickedUsersBadge(UserData.currentSite);
        };

        /**
         * Track user clicks on "Invite users" link within the dropdown of Users Badge.
         */
        this.clickedInviteUsers = function () {
            return headerTracking.clickedInviteUsers(UserData.currentSite);
        };

        /**
         * Track user clicks 'send invites' button on the invite users screen.
         */
        this.clickedSendInvites = headerTracking.clickedSendInvites;

        /**
         * Track user clicks on 'tracking active' badge.
         */
        this.clickedTrackingActive = headerTracking.clickedTrackingActive;

        /**
         * Track user clicks on 'tracking inactive' badge.
         */
        this.clickedTrackingInactive = headerTracking.clickedTrackingInactive;

        /**************************
         * FEEDBACK RELATED EVENTS
         **************************/

        this.createdIncomingFeedback = function () {
            hotjarTracking.trigger('created_incoming_feedback');
        };

        this.createdSurvey = function () {
            hotjarTracking.trigger('created_survey');
        };

        /*************************
         * ACCOUNT + SUBSCRIPTION RELATED EVENTS
         *************************/
        /**
         * Track that a new sub-object of an account has been added.
         * NOTE: This event is specifically for when a new site or Organization is added.
         *
         * @param item 'Organization' or 'Site'.
         */
        this.add = function (item) {
            googleAnalytics.trackCreatedItemEvent('Added ' + item);
        };

        /**
         * Track plan changes for multiple sites
         *
         * @param oldSites {object} A collection of site objects BEFORE the change.
         * @param costSpecs {object} A collection of costSpec objects for the sites we changed.
         */
        this.planChanges = function (changes) {
            changes.forEach((change) => {
                const changeType = change[3];
                self.trigger('plan_' + changeType);
                self.tagRecording(`Plan ${changeType}`);
            });
        };

        /**
         * Track that a user has become a customer (he has paid us money).
         *
         * @param account Account which became a new customer.
         * @param totalCost The total cost which the user spent on his first purchase.
         */
        this.newCustomer = function (account, totalCost) {
            adwords.newCustomer(totalCost);
            facebook.newCustomer(account, totalCost);
            googleAnalytics.trackNewCustomer(totalCost);
        };

        /**
         * Track that a campaign was applied to an account.
         * NOTE: This was used to tag events to Intercom, which is no obsolete. Method is kept for compatibility.
         */
        // eslint-disable-next-line no-unused-vars
        this.campaignApplied = function campaignApplied() {
            // This currently does not track any metrics.
        };

        /**
         * Track that a subscription extension has been granted for a given
         * reason
         * NOTE: This was used to tag events to Intercom, which is no obsolete. Method is kept for compatibility.
         *
         * @param account: The account whose subscription has been extended
         * @param user_id: The ID of the user who requested the extension. This
         * will be the account owner 100% of the time, but its good that this
         * function isn't aware of that.
         * @param reason: The reason attributed to this extension.
         */
        // eslint-disable-next-line no-unused-vars
        this.extensionGranted = function extensionGranted(account, reason) {
            // This currently does not track any metrics.
            return $q.resolve();
        };

        /**
         * Track that a customer has chosen to churn
         * NOTE: This was used to tag events to Intercom, which is no obsolete. Method is kept for compatibility.
         */
        // eslint-disable-next-line no-unused-vars
        this.customerChurned = function customerChurned(reason) {
            // This currently does not track any metrics.
        };

        /**
         * Track non-owner request to send email to owner to notify them
         * of discount when switching to yearly
         * NOTE: This was used to tag events to Intercom, which is no obsolete. Method is kept for compatibility.
         */
        this.switchToYearlyRequest = function switchToYearlyRequest() {
            // This currently does not track any metrics.
        };

        /*************************
         * FEATURE-SPECIFIC EVENT TRACKING
         *************************/

        /**
         * Track in segment each time some data is downloaded as a file.
         *
         * @param item:     the download file name
         * @param fileType: the download file format
         * @param [data] {object} Additional data properties we want to send with the event.
         */
        this.downloadItem = function (item, fileType, data) {
            var valid = validateEventData('Track.downloadItem', [item, fileType]);

            if (valid) {
                data = _.extend(data || {}, self.getGroupDataForSite(UserData.currentSite));
                data['item'] = item;
                data['file_type'] = fileType;

                self.tagRecording('Item Downloaded: ' + item);
                Segment.track('Item Downloaded', data);
            }
        };

        /**
         * Track in segment each time a filter we want to track is used.
         * @param {string} label:   the event name
         * @param {Object} data:    the data attributes related to the filter
         * @param {string} tagLabel:the hj recording tag name
         */
        this.filterUsed = function (label, data, tagLabel) {
            var valid = validateEventData(label, [label, data, tagLabel]);

            if (valid) {
                data = _.extend(data || {}, self.getGroupDataForSite(UserData.currentSite));
                Segment.track(label, data);
                self.tagRecording(tagLabel);
            }
        };

        /**
         * Track user can see the User Attributes options.
         */
        this.userAttributesVisible = function () {
            hotjarTracking.tagRecording('User Attributes: Visible');
            hotjarTracking.trigger('user_attributes_visible');
        };

        /**
         * Track user clicks on the User Attributes radio button in polls.
         */
        this.clickedUserAttributesRadioButtonInPolls = function () {
            hotjarTracking.tagRecording('User Attributes: Clicked radio in polls');
            hotjarTracking.trigger('user_attributes_radio_polls');
        };

        /**
         * Track Incoming Feedback responses view mode changed by user.
         */
        this.incomingFeedbackResponsesViewModeChanged = function (view_mode) {
            const label = 'Incoming Feedback Responses View Mode Changed';
            const valid = validateEventData(label, [view_mode]);

            if (valid) {
                const data = _.extend(
                    {
                        view_mode
                    },
                    self.getGroupDataForSite(UserData.currentSite)
                );
                Segment.track(label, data);
            }
        };

        /**
         * Track Incoming Feedaback respones view mode viewed by user.
         */
        this.incomingFeedbackResponsesViewModeViewed = function (view_mode) {
            const label = 'Incoming Feedback Responses View Mode Viewed';
            const valid = validateEventData(label, [view_mode]);

            if (valid) {
                const data = _.extend(
                    {
                        view_mode
                    },
                    self.getGroupDataForSite(UserData.currentSite)
                );
                Segment.track(label, data);
            }
        };
        /**
         * Track user viewing a list of incoming feedback.
         */
        this.incomingFeedbackListViewed = function (
            number_of_incoming_feedback_in_list,
            number_of_active_incoming_feedback_in_list,
            number_of_inactive_incoming_feedback_in_list,
            text_filter_used
        ) {
            const label = 'Incoming Feedback List Viewed';
            const valid = validateEventData(label, [
                number_of_incoming_feedback_in_list,
                number_of_active_incoming_feedback_in_list,
                number_of_inactive_incoming_feedback_in_list,
                text_filter_used
            ]);

            if (valid) {
                const data = _.extend(
                    {
                        number_of_incoming_feedback_in_list,
                        number_of_active_incoming_feedback_in_list,
                        number_of_inactive_incoming_feedback_in_list,
                        text_filter_used
                    },
                    self.getGroupDataForSite(UserData.currentSite)
                );
                Segment.track(label, data);
            }
        };

        /**
         * Track user dropping a question from the Question Bank into a survey.
         */
        this.addQuestionFromBank = function () {
            const trackData = {
                element_name: 'survey_create_question_bank_question',
                ui_position: 'Survey create/edit Questions section'
            };
            return Segment.track('Question Bank Question Added', trackData);
        };

        /**
         * Track that the Google SSO button was clicked.
         */
        this.googleSignInClicked = function googleSignInClicked() {
            googleAnalytics.trackSignUpWithGoogle();
            self.click('Google Single-sign-on', 'Registration Step 1', {});
            self.tagRecording('google-sso');
        };

        this.liveHeatmapRequested = function ({ type }) {
            const data = self.getGroupDataForSite(UserData.currentSite);
            const userId = UserData.get('userId');
            const eventData = { ...data, user_id: userId, type };

            Segment.track('Live Heatmap Requested', eventData);
        };

        /**
         * Payment and 3DS-specific events.
         * @param {string} amountCharged The amount charged
         * @param {number|undefined} bin Bin number for the payment method.
         */
        this.verify3DSCalled = function (amountCharged, bin) {
            return verify3DSCalled({ amountCharged, bin, account: UserData.currentSite.account });
        };

        /**
         * The result of the 3DS bank challenge attempt.
         * If eligibleFor3DS is false, the transaction was not challenged.
         * If passed3DS is true, the challenge was successful. This does not mean a 3DS challenge was
         * shown - banks can waive transactions through without a challenge.
         * @param {string} amountCharged The amount charged
         * @param {boolean} eligibleFor3DS Transaction is being passed through 3DS
         * @param {boolean} passed3DS Transaction successfully passed 3DS
         * @param {string} threeDSecureVersion Transaction successfully passed 3DS
         */
        this.verify3DSChallenge = function (amountCharged, eligibleFor3DS, passed3DS, threeDSecureVersion) {
            return verify3DSChallenge({
                amountCharged,
                eligibleFor3DS,
                passed3DS,
                threeDSecureVersion,
                account: UserData.currentSite.account
            });
        };

        /**
         * User is passing through the payment authentication flow which might result in 3DS2 challenge.
         * @param {object} context with any data that will be stored on the Track service.
         * @param {Function} fn which result will be wrapped in promise.
         *  After this promise fulfills context will reset to null
         */
        this.withContext = (context, fn) => {
            store.setState({ context });

            // fn() might not return a promise so wrap it in one to ensure we
            // can always finally() on it.
            //
            // This does mean withContext always returns a promise that you can
            // chain on, but it can't be used in synchronous flows.
            return $q
                .all([fn()])
                .then(([result]) => result)
                .finally(() => {
                    store.setState({ context: null });
                });
        };

        this.paymentOrigins = PAYMENT_ORIGINS;

        /**
         * User is passing through the payment authentication flow which might result in 3DS2 challenge
         * @param {boolean} billing3DS2Enabled Whether account has 3DS2 enabled
         */
        this.authenticationNeeded = (billing3DS2Enabled = false) =>
            authenticationNeeded({ billing3DS2Enabled, account: UserData.currentSite.account });
        /**
         * Braintree has returned info on whether client's CC needs additional challenge or is pre-approved
         * @param {string|undefined} reportedStatus The status returned by Braintree.
         * @param {number|undefined} bin Bin number for the payment method.
         * @param {string|undefined} threeDSecureVersion 3ds version of challenge that client will be presented
         */
        this.payment3DS2AuthenticationLookup = (reportedStatus, bin, threeDSecureVersion) => {
            return payment3DS2AuthenticationLookup({
                reportedStatus,
                bin,
                threeDSecureVersion,
                account: UserData.currentSite.account
            });
        };

        /**
         * This event is emitted when using the 3D Secure 2.0 flow and the authentication modal closes,
         *  either because the authentication was completed or because the customer canceled the process.
         */
        this.authenticationModalClosed = () => authenticationModalClosed(UserData.currentSite.account);

        /**
         * This event is emitted when using the 3D Secure 2.0 flow and the authentication modal is rendered.
         */
        this.authenticationModalRendered = () => authenticationModalRendered(UserData.currentSite.account);

        /**
         * This event is emitted when using the 3D Secure 2.0 flow and the customer cancels the 3D Secure challenge.
         */
        this.authenticationModalCustomerCancelled = () =>
            authenticationModalCustomerCancelled(UserData.currentSite.account);

        this.userCreated = (success, plan, errorMessage) => {
            var groupData = {}, // Nothing relevant to group formSubmit event by.
                submitFormData = success ? {} : { app_form_submit_failure_reason: errorMessage };

            if (success) {
                // FE Tracking of new account signups is still needed for some services.
                this.newUserSignup(plan);
            }

            return this.formSubmit('User creation', success, groupData, submitFormData);
        };

        this.userCreatedError = (errorMessage) => {
            return this.formSubmit('User creation', false, {}, { app_form_submit_failure_reason: errorMessage });
        };

        this.accountCreated = () => {
            return this.formSubmit('Account signup', true, {}, {});
        };

        this.accountCreatedError = (errorMessage) => {
            return this.formSubmit('Account signup', false, {}, { app_form_submit_failure_reason: errorMessage });
        };

        this.joinTeam = () => {
            return this.formSubmit('User invite signup', true, {}, {});
        };

        this.joinTeamError = (errorMessage) => {
            return this.formSubmit('User invite signup', false, {}, { app_form_submit_failure_reason: errorMessage });
        };

        /**
         * Company details modal has been submitted
         * @param {Object} args all the parameters that need to based to track function
         * @param {boolean} args.countrySelectChanged whether user has changes their previous country selection
         * @param {boolean} args.taxFieldFilled whether tax field has some value
         * @param {boolean} args.taxFieldPresent whether country has tax field
         * @param {boolean} args.success whether form was successfully submitted
         * @param {string} args.failureReason in case of failure, what was the reason
         */
        this.companyDetailsFormSubmitted = (args = {}) =>
            tracking.companyDetailsFormSubmitted(args, UserData.currentSite.account);

        /**
         * Company details modal has been submitted
         * @param {('existing_user'|'new_user')} userType whether access is requested for new or existing user
         */
        this.requestedSiteAccess = (userType) => {
            Segment.track('Requested Site Access', {
                user_type: userType,
                ...self.getGroupDataForAccount(UserData.currentSite.account)
            });
        };
    });
